import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IOptionItem } from "../../../formik/formik-autocomplete/FormikAutocomplete.types";
import { useClientClaims } from "../../claims/hooks/useClientClaims";

const HOLDING = "clients.client.debtSettlement.assetInventory.assetsTab.holding";

export interface ReferencedClaim {
  claimId: string | null;
  /** The label stored with the reference, shown once the claim is no longer one of the client's claims. */
  storedLabel?: string | null;
}

export function useClaimOptions(clientId: string, references: ReferencedClaim[]): IOptionItem[] {
  const { t } = useTranslation();
  const { data: claims = [] } = useClientClaims(clientId);

  return useMemo(() => {
    const options: IOptionItem[] = claims.map((claim) => ({
      id: claim.id,
      label: claim.caseNumber
        ? t(`${HOLDING}.claimLabel`, {
            creditor: claim.creditorName,
            caseNumber: claim.caseNumber,
          })
        : claim.creditorName,
      isHidden: claim.isCompleted,
    }));

    // A claim an entry already refers to stays resolvable after it was completed or removed, but is not offered.
    const knownIds = new Set(options.map((option) => option.id));
    const removed = new Map<string, string>();
    for (const { claimId, storedLabel } of references) {
      if (claimId && !knownIds.has(claimId) && !removed.has(claimId)) {
        removed.set(claimId, storedLabel ?? t(`${HOLDING}.claimRemoved`));
      }
    }

    return [...options, ...[...removed].map(([id, label]) => ({ id, label, isHidden: true }))];
  }, [claims, references, t]);
}
